import { fetchWithOptions } from 'Utility/fetch';
import { getJWTData, parseJWTFromCookie } from 'Utility/jwtAuthTools';
import { ActionableErrorContext, Loggable, makeLog } from 'Utility/logger';

export const fetchOrganizationSettings = async (): Promise<
  [
    { settings: IOrgSettings } | null,
    { settings: Record<Platform, { allowPatientInitiatedConversations: boolean }> } | null,
  ]
> => {
  const neuroSettingsFetch = fetchWithOptions(
    `/api/settings/org/neuro`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error(`Org settings API server responded with not-ok status ${res.status}`);
      }
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'GET org settings'));
      return null;
    });

  const messagingSettingsFetch = fetchWithOptions(
    `/api/settings/org/messaging_platforms`,
    { neurojwt: parseJWTFromCookie() },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error(`Messaging settings API server responded with not-ok status ${res.status}`);
      }
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'GET messaging platforms'));
      return null;
    });

  return Promise.all([neuroSettingsFetch, messagingSettingsFetch]);
};

export const fetchUserSettings = async (): Promise<{ settings: Record<string, Record<'value', any>> } | null> => {
  return fetchWithOptions(`/api/settings/user/neuro`, { neurojwt: parseJWTFromCookie() }, { method: 'GET' })
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error(`User settings API server responded with not-ok status ${res.status}`);
      }
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'GET user settings Neuro'));
      return null;
    });
};

export const fetchUserSettingsVault = async (): Promise<IUserSettings | null> => {
  const { orgid, useruuid } = getJWTData() ?? {};
  if (!orgid || !useruuid) return null;
  return fetchWithOptions(
    `api/vault/v2/user/`,
    { neurojwt: parseJWTFromCookie(), requestorigin: 'neuro-ui' },
    { method: 'GET' },
  )
    .then((res: Response) => {
      if (res.status === 200) {
        return res.json();
      } else {
        throw new Error(`User settings API server responded with not-ok status ${res.status}`);
      }
    })
    .then(data => {
      return { uiLanguage: data.uILanguage };
    })
    .catch((err: unknown) => {
      makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'GET user settings'));
      return null;
    });
};

export const postUserSettingsVault = async (data: IUserSettings): Promise<boolean[] | null> => {
  const { orgid, useruuid } = getJWTData() ?? { orgid: null, useruuid: null };
  if (!orgid || !useruuid) return null;

  const { uiLanguage, ...rest } = data;

  // At the moment this api accepts uiLang field, but returns uiLanguage so this...
  const uiLanguageObj = uiLanguage ? { uiLang: uiLanguage } : undefined;

  let vaultUpdate = false;

  if (uiLanguageObj) {
    vaultUpdate = uiLanguage
      ? await fetchWithOptions(
          'api/vault/v2/user/',
          { neurojwt: parseJWTFromCookie(), requestorigin: 'neuro-ui' },
          { method: 'PATCH', body: JSON.stringify(uiLanguageObj) },
        )
          .then(res => {
            if (res.status === 200) return true;
            else throw new Error('unexpected status ' + res.status);
          })
          .catch((err: unknown) => {
            makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'PATCH user settings'));
            return false;
          })
      : false;
  }

  let settingsUpdate = false;

  if (Object.keys(rest).length > 0) {
    const evolvedSettings: Record<string, any> = {};
    (Object.keys(rest) as Array<keyof Omit<IUserSettings, 'uiLanguage'>>).forEach(key => {
      evolvedSettings[key] = { value: rest[key] };
    });

    settingsUpdate = await fetchWithOptions(
      'api/settings/user/neuro',
      { neurojwt: parseJWTFromCookie(), requestorigin: 'neuro-ui' },
      { method: 'POST', body: JSON.stringify(evolvedSettings) },
    )
      .then(res => {
        if (res.status === 200) return true;
        else throw new Error('unexpected status ' + res.status);
      })
      .catch((err: unknown) => {
        makeLog(Loggable.BugActionable, err, new ActionableErrorContext(null, 'POST user settings'));
        return false;
      });
  }

  return [vaultUpdate, settingsUpdate];
};
